import React from 'react';

// Every metric on /monitor carries its own explanation. Athira's team kept
// having to ask "what does this number actually count?" (2026-08-19), and a
// native browser tooltip was too slow and too easy to miss — so each metric card
// now shows a small ⓘ in its top-right corner and reveals the plain-English
// calculation on hover.
//
// `className` positions the dot (usually `absolute right-2 top-2` on a card that
// is itself `relative`); the inner span is the positioning context for the
// bubble, so the caller's positioning can never fight with it.
export const InfoDot: React.FC<{
  hint: React.ReactNode;
  className?: string;
  align?: 'right' | 'left';
}> = ({ hint, className, align = 'right' }) => (
  // A NAMED group (`group/info`), because these dots sit inside cards that are
  // themselves `group` — an unnamed group-hover would pop every tooltip open the
  // moment the cursor touched the card.
  <span className={`group/info z-20 ${className || ''}`}>
    <span className="relative flex">
      <span
        aria-hidden="true"
        className="flex h-4 w-4 cursor-help items-center justify-center rounded-full border border-[#E4E0F0] bg-white text-[9px] font-bold leading-none text-slate-400 transition group-hover/info:border-violet-300 group-hover/info:bg-violet-50 group-hover/info:text-violet-600"
      >
        i
      </span>
      <span
        className={`pointer-events-none invisible absolute top-[22px] z-30 w-60 rounded-xl border border-[#E9E6F4] bg-white p-3 text-left text-[11px] font-medium normal-case leading-relaxed tracking-normal text-slate-600 opacity-0 shadow-xl transition-opacity duration-100 group-hover/info:visible group-hover/info:opacity-100 ${
          align === 'right' ? 'right-0' : 'left-0'
        }`}
      >
        {hint}
      </span>
    </span>
  </span>
);

// The standard metric card: a coloured dot, the number, its label, and the ⓘ.
// Shared by the dashboard and the facilitator page so there is exactly one place
// where a metric card's shape is decided.
export const MetricTile: React.FC<{
  value: string;
  label: string;
  tint: string;
  hint?: string;
}> = ({ value, label, tint, hint }) => (
  <div className="relative rounded-xl border border-[#E9E6F4] bg-white px-3 py-2.5">
    <span className={`inline-flex h-6 w-6 items-center justify-center rounded-md text-xs font-bold ${tint}`}>•</span>
    {hint && <InfoDot hint={hint} className="absolute right-2 top-2" />}
    <p className="mt-1.5 text-lg font-bold leading-none tracking-tight text-slate-900">{value}</p>
    <p className="mt-1 text-[11px] font-medium leading-tight text-slate-500">{label}</p>
  </div>
);
